<script setup lang="ts">
import { ref, onMounted, onUnmounted, computed } from 'vue';
import {
  toSSOLogin,
  toSSOLogout,
  toPersonalCenter,
  toEnterpriseCenter,
  toDashBoard,
  toEnterprisePage,
} from '@/configs/coalEduAuth';
import { useUserStore } from '@/stores/user';
import { siteURL } from '@/configs/Constant';
import CommonImage from '../CommonImage.vue';
import { useRouter } from 'vue-router';
import { useLayout } from '@/composables/useLayout';
import { gotoSearchPage, toEnterpriseResource } from '@/logic/gotoPage';
import { isMobile } from '@/utils/platform';

const { isShowheader, menuList, activeMenu, showMobileMenu, layheader } = useLayout();

const displayMenuList = computed<MenuItem[]>(() => {
  if (!isMobile.value) {
    return menuList.value;
  }

  const flattenedList: MenuItem[] = [];

  menuList.value.forEach((menu) => {
    if (menu.name === '加入我们' && menu.dropdownItems?.length) {
      flattenedList.push({
        ...menu,
        hasDropdown: false,
        dropdownItems: undefined,
      });

      menu.dropdownItems.forEach((item) => {
        flattenedList.push({
          name: item.name,
          link: item.link,
          target: item.target,
        });
      });
    } else {
      flattenedList.push(menu);
    }
  });

  return flattenedList;
});
// 类型定义
interface DropdownItem {
  name: string;
  link: string;
  target?: string;
}

interface MenuItem {
  name: string;
  link: string;
  target?: string;
  hasDropdown?: boolean;
  showDropdown?: boolean;
  dropdownItems?: DropdownItem[];
}

interface UserDropdownItem {
  label: string;
  action: string;
  className?: string;
}

const userStore = useUserStore();
const router = useRouter();
// 响应式数据
const searchQuery = ref('');
const showUserDropdown = ref(false);

// 用户下拉菜单项
const userDropdownItems: UserDropdownItem[] = [
  { label: '个人中心', action: 'personal-center' },
  { label: '企业培训', action: 'enterprise-training', className: 'enterprise-training' },
  { label: '企业资源中心', action: 'enterpriseResource', className: '' },
  { label: '退出登录', action: 'logout' },
];

// 计算属性
const userAvatar = computed(() => {
  return userStore.userInfo.avatarSrc && userStore.userInfo.avatarSrc.trim() !== ''
    ? userStore.userInfo.avatarSrc
    : '@/assets/images/header/avatar.png';
});

const visibleDropdownItems = computed(() => {
  return userDropdownItems.filter((item) => {
    if (item.action === 'enterprise-training') {
      return userStore.isEnterpriseManager;
    }
    if (item.action === 'enterpriseResource') {
      return userStore.isEnterpriseMember;
    }
    return true;
  });
});

const hasBannerClass = computed(() => {
  const currentPath = window.location.pathname;
  if (
    currentPath.includes('/courseDetails.html') ||
    currentPath.includes('/specialDetails.html') ||
    currentPath.includes('/liveDetails.html') ||
    currentPath.includes('/categoryIndex.html') ||
    currentPath.includes('/liveDetails.html') ||
    currentPath.includes('/coursePlay.html') ||
    currentPath.includes('/livePlayback.html') ||
    currentPath.includes('/livePlay.html') ||
    currentPath.includes('/liveAssistantPlay.html')
  ) {
    return false;
  }
  return true;
});

// Header背景样式计算属性
const headerBackgroundStyle = computed(() => {
  const currentPath = window.location.pathname;

  // 只对knowledgeLibrary页面使用特定颜色
  if (
    currentPath.includes('/courseDetails.html') ||
    currentPath.includes('/specialDetails.html') ||
    currentPath.includes('/liveDetails.html') ||
    currentPath.includes('/categoryIndex.html') ||
    currentPath.includes('/liveDetails.html') ||
    currentPath.includes('/coursePlay.html') ||
    currentPath.includes('/livePlayback.html') ||
    currentPath.includes('/livePlay.html') ||
    currentPath.includes('/liveAssistantPlay.html')
  ) {
    const background =
      isMobile.value && showMobileMenu.value ? 'rgba(0, 0, 0, 0.8)' : 'rgba(0, 0, 0, 0.2)';
    const skiceobj =
      isMobile.value && showMobileMenu.value
        ? {
          position: 'sticky',
          top: 0,
        }
        : {};
    return {
      background: background,
      ...skiceobj,
    };
  }

  // 其他页面使用默认颜色
  return {
    background: '#15459e',
    backdropFilter: 'blur(10px)',
  };
});

// 获取当前路径并设置高亮菜单
const getCurrentPath = () => {
  const currentPath = window.location.pathname;
  const currentSearch = window.location.search;
  const fullPath = currentPath + currentSearch;

  if (currentPath.includes('courseDetails') || currentPath.includes('coursePlay')) {
    return `${siteURL}html/courseIndex.html`;
  }

  if (
    currentPath.includes('liveDetail') ||
    currentPath.includes('livePlay') ||
    currentPath.includes('livePlayback')
  ) {
    return `${siteURL}html/liveList.html`;
  }

  const matchesLink = (link: string) => {
    if (!link) {
      return false;
    }

    if (link === siteURL) {
      return currentPath === '/' || currentPath === '/html/' || currentPath === '/html';
    }

    if (link === 'data-dashboard') {
      return false;
    }

    if (link.includes('html/')) {
      const relativePath = link.replace(siteURL, '');
      return currentPath.includes(relativePath);
    }

    if (!link.startsWith('http') && !link.includes('html/')) {
      return currentPath.includes(link);
    }

    if (link.includes('jncy')) {
      return currentPath.includes('jncy');
    }

    if (link.startsWith(siteURL)) {
      const relativePath = link.replace(siteURL, '');
      if (!relativePath) {
        return currentPath === '/' || currentPath === '/html/' || currentPath === '/html';
      }
      return currentPath.includes(relativePath);
    }

    return fullPath === link || currentPath === link;
  };

  for (const menu of menuList.value) {
    if (matchesLink(menu.link)) {
      return menu.link;
    }

    if (menu.dropdownItems?.length) {
      for (const item of menu.dropdownItems) {
        if (matchesLink(item.link)) {
          return isMobile.value ? item.link : menu.link;
        }
      }
    }
  }

  return '';
};

// 方法
const handleLogoClick = (event: Event) => {
  event.preventDefault();
  activeMenu.value = siteURL;
  location.href = siteURL;
};

const handleMenuClick = (menu: MenuItem, event?: Event) => {
  // 更新高亮状态
  activeMenu.value = menu.link;

  if (menu.link === 'data-dashboard') {
    toDashBoard();
    return;
  }

  if (showMobileMenu.value) {
    showMobileMenu.value = false;
  }

  if (menu.target === '_blank') {
    console.log('在新窗口打开:', menu.link);
    // 调用jumpToBlank函数
    emit('external-link', menu.link);
    return;
  }
  location.href = menu.link;
};

const toggleMobileMenu = () => {
  showMobileMenu.value = !showMobileMenu.value;
  // console.log('切换移动端菜单:', showMobileMenu.value);
};

const handleMenuHover = (menu: MenuItem) => {
  if (menu.hasDropdown) {
    menu.showDropdown = true;
  }
};

const handleMenuLeave = (menu: MenuItem) => {
  if (menu.hasDropdown) {
    menu.showDropdown = false;
  }
};

const handleDropdownItemClick = (item: DropdownItem) => {
  if (item.target === '_blank') {
    emit('external-link', item.link);
    return;
  }
  location.href = item.link;
};

const performSearch = () => {
  const query = searchQuery.value.trim();
  gotoSearchPage(query);
};

const handleSearchKeydown = (event: KeyboardEvent) => {
  if (event.key === 'Enter') {
    performSearch();
  }
};

const handleRegister = () => {
  try {
    console.log('点击注册按钮');
    emit('register-click');
  } catch (error) {
    console.error('注册失败:', error);
  }
};

const handleUserAction = async (action: string) => {
  const isLogin = await userStore.checkLoginStatus();
  if (!isLogin) {
    await ElMessageBox.confirm('您暂未登录，是否登录？', '提示', {
      confirmButtonText: '登录',
      cancelButtonText: '取消',
      type: 'warning',
    }).then(() => {
      toSSOLogin(null);
    });
    return;
  }
  try {
    switch (action) {
      case 'personal-center':
        toPersonalCenter();
        break;
      case 'enterprise-training':
        toEnterprisePage();
        break;
      case 'enterprise-center':
        toEnterpriseCenter();
        break;
      case 'enterpriseResource':
        toEnterpriseResource();
        break;
      case 'dashboard':
        toDashBoard();
        break;
      case 'logout':
        toSSOLogout();
        break;
    }
  } catch (error) {
    console.error('菜单操作失败:', error);
  }
  showUserDropdown.value = false;
};

const toggleUserDropdown = (event: Event) => {
  event.stopPropagation();
  showUserDropdown.value = !showUserDropdown.value;
};

const handleClickOutside = (event: Event) => {
  const target = event.target as Element;
  if (!target.closest('.menu-user')) {
    showUserDropdown.value = false;
  }
};

const handleEscapeKey = (event: KeyboardEvent) => {
  if (event.key === 'Escape') {
    showUserDropdown.value = false;
    showMobileMenu.value = false;
  }
};

const emit = defineEmits<{
  'menu-change': [link: string];
  search: [query: string];
  'register-click': [];
  'external-link': [link: string];
}>();

// 更新菜单高亮状态
const updateActiveMenu = () => {
  activeMenu.value = getCurrentPath();
};

// 监听路由变化（适用于单页应用）
const handleRouteChange = () => {
  updateActiveMenu();
};

// 生命周期
onMounted(() => {
  document.addEventListener('click', handleClickOutside);
  document.addEventListener('keydown', handleEscapeKey);
  // 监听popstate事件（浏览器前进后退）
  window.addEventListener('popstate', handleRouteChange);
  // 初始化时设置当前路径对应的高亮菜单
  updateActiveMenu();
});

onUnmounted(() => {
  document.removeEventListener('click', handleClickOutside);
  document.removeEventListener('keydown', handleEscapeKey);
  window.removeEventListener('popstate', handleRouteChange);
});
</script>
<template>
  <div class="layout-header" ref="layheader" v-if="isShowheader" :style="headerBackgroundStyle">
    <a href="#" class="logo" @click="handleLogoClick">
      <img src="@/assets/images/header/logo@2x.png" alt="logo" />
    </a>

    <div class="menu-list" id="menuList" :class="{ active: showMobileMenu }">
      <div class="menu-search-mobile">
        <input v-model="searchQuery" type="text" class="search-input" id="headerSearchMobile" placeholder="搜索"
          @keydown="handleSearchKeydown" />
        <div class="search-icon" @click="performSearch">
          <img src="@/assets/images/header/search.png" alt="搜索" width="21" height="21" />
        </div>
      </div>
      <div v-for="menu in displayMenuList" :key="menu.link" class="menu-item"
        :class="{ active: activeMenu === menu.link, 'has-dropdown': menu.hasDropdown }" :data-link="menu.link"
        :data-target="menu.target" @click="handleMenuClick(menu, $event)" @mouseenter="handleMenuHover(menu)"
        @mouseleave="handleMenuLeave(menu)">
        {{ menu.name }}
        <div v-if="menu.hasDropdown" class="menu-dropdown"
          :class="{ show: menu.showDropdown, 'menu-dropdown-nobanner': hasBannerClass }">
          <div v-for="item in menu.dropdownItems" :key="item.link" class="dropdown-item"
            @click.stop="handleDropdownItemClick(item)">
            {{ item.name }}
          </div>
        </div>
      </div>
    </div>

    <div class="header-right">
      <!-- 汉堡菜单图标 -->
      <div class="hamburger-menu" id="hamburgerMenu" @click="toggleMobileMenu">
        <span></span>
        <span></span>
        <span></span>
      </div>

      <div class="menu-search">
        <input v-model="searchQuery" type="text" class="search-input" id="headerSearch" placeholder="搜索"
          @keydown="handleSearchKeydown" />
        <div class="search-icon" @click="performSearch">
          <img src="@/assets/images/header/search.png" alt="搜索" width="21" height="21" />
        </div>
      </div>

      <!-- 未登录状态 -->
      <div v-if="!userStore.isLogin" class="menu-notLogin-btns" id="menuNotLoginBtns">
        <div class="login-btn" @click="toSSOLogin">登录</div>
        <div class="register-btn" @click="handleRegister">注册</div>
      </div>

      <!-- 已登录状态 -->
      <div v-else class="menu-user" @click="toggleUserDropdown">
        <CommonImage :src="userAvatar" error-image="header/avatar.png" alt="用户头像" class="user-avatar" />
        <div class="user-info">
          <div class="user-name">
            <div id="login-username" :title="userStore.userInfo.userName">{{ userStore.userInfo.userName }}</div>
            <img src="@/assets/images/header/arrowWhite.png" alt="arrow-down" class="dropdown-arrow"
              :class="{ rotated: showUserDropdown }" />
          </div>
          <div class="user-role" id="user-role">{{ userStore.userRole }}</div>
        </div>
        <div class="user-dropdown" :class="{ show: showUserDropdown }">
          <div v-for="item in visibleDropdownItems" :key="item.action" class="dropdown-item" :class="item.className"
            @click="handleUserAction(item.action)">
            {{ item.label }}
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss">
/* 头部 */
.layout-header {
  position: relative;
  z-index: 99;
  height: $header-height;
  display: flex;
  align-items: center;
  justify-content: space-between;
  background: #15459e;
  /* 移除固定背景色，使用动态样式 */
  padding: 0 max(15px, 0.3rem);
  // transform: translateY(-100%);
  // animation: slideInFromTop 1.5s cubic-bezier(0.25, 0.46, 0.45, 0.94) forwards;
}

@keyframes slideInFromTop {
  from {
    transform: translateY(-100%);
  }

  to {
    transform: translateY(0);
  }
}

.logo {
  width: 4.4rem;
  height: 0.63rem;
  flex-shrink: 0;
  margin-right: 40px;
  text-decoration: none;
}

.logo>img {
  width: 100%;
  height: 100%;
  object-fit: contain;
  display: block;
}

.menu-list {
  display: flex;
  align-items: center;
  justify-content: center;
  flex: 1;
  min-width: 0;
  margin: 0 20px;
  height: 100%;
}

.menu-search-mobile {
  display: none;
}

.menu-item {
  position: relative;
  color: #ffffff;
  opacity: 0.8;
  height: 100%;
  font-size: max(12px, 0.2rem);
  padding: 0 max(8px, 0.15rem);
  display: flex;
  align-items: center;
  cursor: pointer;
  transition: all 0.3s ease;
  border-bottom: 2px solid transparent;
  white-space: nowrap;
  flex-shrink: 0;
  min-width: max-content;
  box-sizing: border-box;
}

.menu-item:hover {
  opacity: 1;
  background-color: rgba(255, 255, 255, 0.1);
}

.menu-item.active {
  opacity: 1;
}

.menu-item.active::after {
  content: '';
  position: absolute;
  bottom: -2px;
  width: 50%;
  left: 50%;
  transform: translateX(-50%);
  height: 3px;
  background-color: #ffffff;
}

/* 菜单下拉样式 */
.menu-item.has-dropdown {
  position: relative;
}

.layout-header {
  .menu-dropdown {
    position: absolute;
    top: calc(100% + 1px);
    width: 100%;
    left: 0;
    background: rgba(0, 0, 0, 0.2);
    min-width: max-content;
    height: $header-height;
    margin-top: 1px;
    opacity: 0;
    visibility: hidden;
    transition: all 0.3s ease;
    z-index: 1000;
    display: flex;
    flex-direction: column;
  }

  .menu-dropdown-nobanner {
    background-color: #15459e !important;
  }

  .menu-dropdown.show {
    opacity: 1;
    visibility: visible;
    transform: translateY(0);
  }

  .menu-dropdown .dropdown-item {
    position: relative;
    color: #ffffff;
    height: 100%;
    min-height: $header-height;
    font-size: max(12px, 0.18rem);
    padding: 0 max(8px, 0.1rem);
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    transition: all 0.3s ease;
    border-bottom: 2px solid transparent;
    white-space: nowrap;
    flex-shrink: 0;
    min-width: max-content;
  }

  .menu-dropdown .dropdown-item:hover {
    background-color: rgba(255, 255, 255, 0.1);
  }
}

.header-right {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  height: 100%;
}

/* 汉堡菜单样式 */
.hamburger-menu {
  margin-right: 10px;
  display: none;
  flex-direction: column;
  justify-content: space-around;
  width: 30px;
  height: 25px;
  background: transparent;
  border: none;
  cursor: pointer;
  padding: 0;
  z-index: 100;
}

.hamburger-menu span {
  width: 100%;
  height: 3px;
  background-color: #ffffff;
  border-radius: 2px;
  transition: all 0.3s ease;
}

/* 搜索框样式 */
.menu-search {
  display: flex;
  margin-right: max(10px, 0.3rem);
  position: relative;
  height: 40px;
}

.search-input {
  width: max(160px, 2rem);
  border: none;
  height: 40px;
  box-sizing: border-box;
  border-radius: 20px;
  padding: 0 50px 0 20px;
  background: #ffffff;
  font-size: 14px;
  outline: none;
  transition: all 0.3s ease;
}

.search-input::placeholder {
  color: #999c9f;
  font-size: 14px;
}

.search-input:focus {
  box-shadow: 0 0 0 2px rgba(0, 123, 255, 0.25);
}

.search-icon {
  position: absolute;
  right: 20px;
  top: 50%;
  transform: translateY(-50%);
  width: 21px;
  height: 21px;
  cursor: pointer;
  transition: all 0.3s ease;
}

.search-icon:hover {
  transform: translateY(-50%) scale(1.1);
}

/* 搜索图片样式 */
.search-icon img {
  display: block;
  width: 100%;
  height: 100%;
  object-fit: contain;
  transition: all 0.3s ease;
}

/* 登录注册按钮 */
.menu-notLogin-btns {
  display: flex;
  align-items: center;
}

.login-btn,
.register-btn {
  text-align: center;
  line-height: 40px;
  color: #ffffff;
  width: 60px;
  font-size: 12px;
  height: 40px;
  background-size: contain !important;
  cursor: pointer;
  transition: all 0.3s ease;
  position: relative;
  transform: translateZ(0);
}

.login-btn {
  background: url('@/assets/images/header/login.png') no-repeat center center;
}

.register-btn {
  margin-top: 3px;
  margin-left: -17px;
  background: url('@/assets/images/header/register.png') no-repeat center center;
}

/* Hover 效果 */
.login-btn:hover,
.register-btn:hover {
  transform: translateY(-2px) scale(1.05);
  filter: brightness(1.1) saturate(1.2);
}

.login-btn:active,
.register-btn:active {
  transform: translateY(0) scale(0.98);
  transition: all 0.1s ease;
}

/* 用户信息样式 */
.menu-user {
  position: relative;
  display: flex;
  align-items: center;
  cursor: pointer;
  padding: 0 max(10px, 0.2rem);
  border-radius: 8px;
  transition: background-color 0.3s ease;
  height: 100%;
}

.menu-user:hover {
  background-color: rgba(255, 255, 255, 0.1);
}

.user-avatar {
  width: 42px;
  height: 42px;
  border-radius: 50%;
  margin-right: 12px;
  object-fit: cover;
}

.user-info {
  color: #ffffff;
}

.user-name {
  font-size: 16px;
  font-weight: 500;
  display: flex;
  align-items: center;
  height: 24px;
}

.user-name {
  #login-username {
    max-width: 84px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;

    @media (max-width:769px) {
      max-width: 44px;

    }
  }
}

.user-role {
  margin-top: 6px;
  font-size: 12px;
  opacity: 0.6;
  height: 18px;
  display: flex;
  align-items: center;
}

.user-info .dropdown-arrow {
  margin-left: 8px;
  width: 10px;
  height: 10px;
  transform: rotate(180deg);
  transition: transform 0.3s ease;
}

.menu-user:hover .dropdown-arrow,
.dropdown-arrow.rotated {
  transform: rotate(0deg);
}

/* 下拉菜单 */
.user-dropdown {
  position: absolute;
  top: 100%;
  right: 0;
  background: #ffffff;
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  width: 150px;
  opacity: 0;
  visibility: hidden;
  transform: translateY(-10px);
  transition: all 0.3s ease;
  z-index: 1000;
}

.menu-user:hover .user-dropdown,
.user-dropdown.show {
  opacity: 1;
  visibility: visible;
  transform: translateY(0);
}

.dropdown-item {
  padding: 12px 16px;
  color: #666666;
  font-size: 14px;
  cursor: pointer;
  border-bottom: 1px solid #f0f0f0;
  transition: background-color 0.2s ease;
}

.dropdown-item:last-child {
  border-bottom: none;
}

.dropdown-item:hover {
  background-color: #ebf1ff;
  color: #000000;
}

.dropdown-item:first-child {
  border-radius: 8px 8px 0 0;
}

.dropdown-item:last-child {
  border-radius: 0 0 8px 8px;
}

/* 企业相关菜单项默认隐藏 */
.dropdown-item.enterprise-training,
.dropdown-item.enterprise-center {
  display: none;
}

/* 当有企业权限时显示 */
.dropdown-item.enterprise-training:not(.hidden),
.dropdown-item.enterprise-center:not(.hidden) {
  display: block;
}

/* 响应式设计 */
@media (max-width: 1366px) {
  .menu-item {
    padding: 0 5px;
  }
}

@media (max-width: 1200px) {
  .menu-item {
    padding: 0 max(8px, 0.1rem);
  }

  .menu-search {
    display: none;
  }

  .menu-search-mobile {
    display: noen;
    position: relative;
    width: 100%;
    margin: 0 20px 20px 20px;
    height: 40px;
  }

  .menu-search-mobile .search-input {
    width: 100%;
    border: 1px solid #e0e0e0;
    height: 40px;
    box-sizing: border-box;
    border-radius: 20px;
    padding: 0 50px 0 20px;
    background: #ffffff;
    font-size: 14px;
    outline: none;
    transition: all 0.3s ease;
  }

  .menu-search-mobile .search-input::placeholder {
    color: #999c9f;
    font-size: 14px;
  }

  .menu-search-mobile .search-input:focus {
    border-color: #15459e;
    box-shadow: 0 0 0 2px rgba(21, 69, 158, 0.1);
  }

  .menu-search-mobile .search-icon {
    position: absolute;
    right: 20px;
    top: 50%;
    transform: translateY(-50%);
    width: 21px;
    height: 21px;
    cursor: pointer;
    transition: all 0.3s ease;
  }

  .menu-search-mobile .search-icon:hover {
    transform: translateY(-50%) scale(1.1);
  }

  .menu-search-mobile .search-icon img {
    display: block;
    width: 100%;
    height: 100%;
    object-fit: contain;
  }
}

/* 响应式设计 */
@media (max-width: 1024px) {
  .layout-header {
    min-width: auto;
    padding: 0 20px;
  }

  .layout-header .logo {
    height: auto;
    margin-right: 20px;
  }

  .menu-list {
    margin: 0 10px;
  }

  .menu-item {
    font-size: 14px;
    padding: 0 2px;
  }
}

@media (max-width: 900px) {
  .layout-header {
    z-index: 1001;
  }

  .layout-header .logo {
    width: 50%;
  }

  .layout-header {
    padding: 0 15px;
  }

  .hamburger-menu {
    display: flex;
  }

  .menu-list {
    position: fixed;
    top: $header-height;
    left: 0;
    right: 0;
    background-color: #fffffff2;
    flex-direction: column;
    justify-content: flex-start;
    margin: 0;
    padding: 20px 0;
    transform: translateY(-100%);
    opacity: 0;
    visibility: hidden;
    height: calc(100vh - $header-height);
    overflow-y: auto;
  }

  .menu-list.active {
    transform: translateY(0);
    opacity: 1;
    visibility: visible;
  }

  .menu-search-mobile {
    position: relative;
    width: calc(100% - 40px);
    margin: 0 20px 20px 20px;
    order: -1;
    /* 确保搜索框显示在菜单顶部 */
    display: flex;
  }

  .menu-search-mobile .search-input {
    width: 100%;
    border: 1px solid #cdcdcd;
    background: #ffffff;
  }

  .menu-search-mobile .search-icon img {
    filter: brightness(0) saturate(100%) invert(28%) sepia(95%) saturate(1794%) hue-rotate(204deg) brightness(94%) contrast(96%);
  }

  .has-dropdown {
    display: flex;
    align-items: center;
  }

  .has-dropdown .menu-dropdown {
    position: relative;
    opacity: 1;
    visibility: visible;
    top: initial;
    width: 85% !important;
    left: inherit;
    background: transparent !important;
    height: 40px;
    margin-top: 4px;
    border-bottom: 1px solid #cdcdcd;
    margin-left: 10px;
    display: none;

    .dropdown-item {
      color: inherit;
      min-height: inherit !important;
      text-align: center;
      font-size: 14px;
    }
  }

  .menu-item {
    min-height: 22px;
    font-size: min(0.5rem, 16px);
    padding: 20px;
    text-align: left;
    width: 100%;
    color: #292f40;
    height: auto;
    border-bottom: 1px solid #cdcdcd;
  }

  .menu-list.active .menu-item.active {
    color: #083286;
  }

  .menu-item.active::after {
    display: none;
  }

  .menu-item:last-child {
    border-bottom: none;
  }

  .header-right {
    margin-left: auto;
  }

  /* 中等屏幕登录按钮优化 */
  .login-btn,
  .register-btn {
    width: 55px;
    height: 36px;
    line-height: 36px;
    font-size: 11px;
  }

  .register-btn {
    margin-left: -15px;
    margin-top: 2px;
  }
}

@media (max-width: 480px) {
  .layout-header .logo {
    width: 4rem;
    margin-right: 0;
  }

  .hamburger-menu {
    width: 20px;
    height: 20px;
    margin-right: 5px;
  }

  .menu-user {
    margin-left: 0;
  }

  .user-role {
    display: none;
  }

  .user-avatar {
    width: 30px;
    height: 30px;
  }

  .user-name {
    font-size: 12px;
  }

  /* 小屏设备登录按钮优化 */
  .login-btn,
  .register-btn {
    width: 50px;
    height: 32px;
    line-height: 32px;
    font-size: 11px;
  }

  .register-btn {
    margin-left: -14px;
    margin-top: 2px;
  }
}
</style>
